import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { isOwnerOnlyPath, TopBar } from '@/components/layout/top-bar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { authClient } from '@/lib/auth-client';
import { useActiveOrg } from '@/lib/use-active-org';

export function AuthedLayout() {
  const navigate = useNavigate();
  const { data: session, isPending } = authClient.useSession();
  const currentPath = useRouterState({ select: (s) => s.location.pathname });
  const activeOrganizationId = session?.session.activeOrganizationId ?? null;
  const userId = session?.user.id ?? null;
  const { isOwner, roleKnown } = useActiveOrg();
  // Only meaningful while activeOrganizationId is null (see the effect below).
  const [inviteLookup, setInviteLookup] = useState<'checking' | 'settled'>('checking');

  // Redirect to login if not authenticated.
  useEffect(() => {
    if (!isPending && !session) {
      void navigate({ to: '/login' });
    }
  }, [isPending, session, navigate]);

  // A signed-in user with NO active workspace is the invited-but-not-yet-joined
  // case: ensureWorkspaceForUser (packages/db) deliberately skips creating a
  // personal workspace while an invitation is pending, so there is nothing to
  // pin. Every /api call 401s in that state (middleware/tenancy.ts rejects a
  // session with no active organization), so send them to the invitation
  // instead of rendering a dashboard that cannot load.
  //
  // Keyed on primitives, NOT the session object: this effect sets state, so a
  // dependency whose identity changed every render would re-fetch forever.
  useEffect(() => {
    if (isPending || !userId || activeOrganizationId) return;
    let cancelled = false;
    setInviteLookup('checking');
    void (async () => {
      const { data } = await authClient.organization.listUserInvitations();
      if (cancelled) return;
      // The endpoint filters to status=pending but not to unexpired, and it
      // 403s for unverified users (data undefined) — both land on 'settled',
      // which explains the state rather than looping.
      const now = Date.now();
      const live = (data ?? []).find((invite) => new Date(invite.expiresAt).getTime() > now);
      if (!live) {
        setInviteLookup('settled');
        return;
      }
      void navigate({
        to: '/accept-invitation/$invitationId',
        params: { invitationId: live.id },
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [isPending, userId, activeOrganizationId, navigate]);

  // Sources and Settings are owner-only. Hiding the nav links (TopBar) is not a
  // guard on its own — a typed URL or a stale tab still lands here — so bounce a
  // member back to the one page they can use. Gated on roleKnown: isOwner reads
  // false while the roster loads, which would bounce the owner too.
  useEffect(() => {
    if (!roleKnown || isOwner || !isOwnerOnlyPath(currentPath)) return;
    void navigate({ to: '/dashboard', replace: true });
  }, [roleKnown, isOwner, currentPath, navigate]);

  async function handleLogout() {
    await authClient.signOut();
    toast.success('Signed out');
    void navigate({ to: '/login' });
  }

  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="space-y-2 text-center">
          <Skeleton className="mx-auto h-8 w-32" />
          <Skeleton className="mx-auto h-4 w-48" />
        </div>
      </div>
    );
  }

  if (!session) {
    // Show nothing while the redirect kicks in.
    return null;
  }

  if (!activeOrganizationId) {
    // Either the redirect above is still resolving, or no usable invitation was
    // found. The latter is recoverable without support: signing out and back in
    // re-runs ensureWorkspaceForUser, which now sees no pending invitation and
    // creates the personal workspace.
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        {inviteLookup === 'checking' ? (
          <Skeleton className="h-8 w-32" />
        ) : (
          <div className="w-full max-w-md rounded-xl border border-border bg-background p-8 text-center shadow-sm">
            <h1 className="text-lg font-semibold">No workspace yet</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              You were invited to a workspace but haven&apos;t joined it yet. Open the invitation
              link from your email to accept it, or sign in again to start a workspace of your own.
            </p>
            <Button
              className="mt-6 w-full"
              variant="outline"
              onClick={() => {
                void handleLogout();
              }}
            >
              Sign out
            </Button>
          </div>
        )}
      </div>
    );
  }

  // The design keeps the bar but hides nav while the user is still setting up.
  const isOnboarding = currentPath.startsWith('/onboarding');

  return (
    <div className="flex min-h-screen flex-col">
      <TopBar showNav={!isOnboarding} isOwner={isOwner} onSignOut={handleLogout} />

      {/* Page content */}
      <main className="mx-auto w-full max-w-[1080px] flex-1 px-10 py-12">
        <Outlet />
      </main>
    </div>
  );
}
